import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Users, ArrowRight } from 'lucide-react';
import { useCommunitySessionsHook } from '@/hooks/useCommunitySessionsHook';
import { CommunitySessionCard } from '@/components/community/CommunitySessionCard';

const SkeletonCard = () => (
  <div className="rounded-lg border border-border/50 bg-card p-6 animate-pulse">
    <div className="flex items-start justify-between gap-2 mb-4">
      <div className="h-6 bg-muted rounded w-3/4"></div>
      <div className="h-10 w-10 bg-muted rounded"></div>
    </div>
    <div className="flex gap-2 mb-4">
      <div className="h-5 bg-muted rounded w-16"></div>
      <div className="h-5 bg-muted rounded w-20"></div>
    </div>
    <div className="space-y-2">
      <div className="h-4 bg-muted rounded w-full"></div>
      <div className="h-4 bg-muted rounded w-2/3"></div>
    </div>
  </div>
);

export const LandingCommunitySessions: React.FC = () => {
  const { communitySessions, loading, error } = useCommunitySessionsHook(undefined, true);

  // Fisher-Yates shuffle, memoized to run once when sessions change
  const shuffledSessions = useMemo(() => {
    if (communitySessions.length === 0) return [];
    const shuffled = [...communitySessions];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled.slice(0, 3);
  }, [communitySessions]);

  // Don't render section if no sessions available (after loading)
  if (!loading && (error || shuffledSessions.length === 0)) {
    return null;
  }

  return (
    <>
      {/* Section Header */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center justify-center h-16 w-16 bg-primary-soft rounded-full mb-6 mx-auto">
          <Users className="h-8 w-8 text-primary" />
        </div>
        <h2 className="text-3xl font-light text-foreground mb-4">
          Community Sessions
        </h2>
        <p className="text-muted-foreground font-light max-w-2xl mx-auto mb-8">
          Discover meditation sessions shared by our community members. Browse, listen, and find inspiration
          from sessions created by practitioners and users just like you.
        </p>
      </div>

      {/* Sessions Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {shuffledSessions.map((session) => (
            <CommunitySessionCard
              key={session.id}
              session={session}
            />
          ))}
        </div>
      )}

      {/* View All Button */}
      <div className="text-center">
        <Button
          asChild
          size="lg"
          variant="outline"
          className="meditation-button border-primary/30 text-primary hover:bg-primary/10 font-light px-8 py-3 text-lg rounded-full"
        >
          <Link to="/community">
            <Users className="mr-2 h-5 w-5" />
            Browse All Community Sessions
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </Button>
      </div>
    </>
  );
};
